import { useCallback, useEffect, useRef } from 'react';
import { useSite } from 'context/SiteContext';
import { useFetchKioskAppointments } from 'query/public';
import {
  KIOSK_GENERAL_ERROR,
  KIOSK_MULTIPLE_RESULTS_ERROR,
  KIOSK_NO_APPTS_FOUND_ERROR,
  KioskFindAppointmentErrorState,
} from './useKioskState';
import { KioskAppointment } from 'types/appointments';

export type KioskApptSearchParams = {
  searchString: string;
  type: 'PHONE' | 'BIRTHDATE' | 'BIRTHDATE_SURNAME_INITIAL';
};

const inputTypeMap = {
  PHONE: 'phone',
  BIRTHDATE: 'birthdate',
  BIRTHDATE_SURNAME_INITIAL: 'birthdate_surname_initial',
} as const;

export type KioskFindAppointmentHandlers = {
  onError: (errorType: KioskFindAppointmentErrorState) => void;
  onSuccess: (appt: KioskAppointment) => void;
};

export const useKioskFindAppointment = () => {
  const { activeSite } = useSite();
  const fetchKioskAppointments = useFetchKioskAppointments();
  // Only the latest search may move the kiosk. A newer search or a cancel makes
  // the results of older searches stale.
  const latestSearchId = useRef(0);

  const cancelFindAppointment = useCallback(() => {
    latestSearchId.current += 1;
  }, []);

  useEffect(() => cancelFindAppointment, [cancelFindAppointment]);

  const findAppointment = useCallback(
    async (
      { type, searchString }: KioskApptSearchParams,
      { onError, onSuccess }: KioskFindAppointmentHandlers,
    ) => {
      latestSearchId.current += 1;
      const searchId = latestSearchId.current;
      let appts: KioskAppointment[];
      try {
        const response = await fetchKioskAppointments({
          siteId: activeSite.id,
          input: searchString,
          inputType: inputTypeMap[type],
        });
        appts = response?.data?.data || [];
      } catch {
        if (searchId === latestSearchId.current) {
          onError(KIOSK_GENERAL_ERROR);
        }
        return;
      }
      if (searchId !== latestSearchId.current) {
        return;
      }
      if (!appts.length) {
        onError(KIOSK_NO_APPTS_FOUND_ERROR);
      } else if (appts.length > 1) {
        onError(KIOSK_MULTIPLE_RESULTS_ERROR);
      } else {
        onSuccess(appts[0]);
      }
    },
    [activeSite, fetchKioskAppointments],
  );

  return { findAppointment, cancelFindAppointment };
};
